import React from 'react';

interface PaginationProps {
  page: number;
  totalPages: number;
  total: number;
  onPageChange: (page: number) => void;
}

export const Pagination: React.FC<PaginationProps> = ({
  page,
  totalPages,
  total,
  onPageChange,
}) => {
  if (totalPages <= 1) return null;

  const pages = (): (number | string)[] => {
    const result: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) result.push(i);
      return result;
    }
    result.push(1);
    if (page > 3) result.push('...');
    const start = Math.max(2, page - 1);
    const end = Math.min(totalPages - 1, page + 1);
    for (let i = start; i <= end; i++) result.push(i);
    if (page < totalPages - 2) result.push('...');
    result.push(totalPages);
    return result;
  };

  const buttonStyle = (isActive: boolean, isDisabled = false): React.CSSProperties => ({
    minWidth: 32,
    height: 32,
    padding: '0 10px',
    fontSize: 13,
    fontWeight: 500,
    fontFamily: 'var(--font-family)',
    borderRadius: 'var(--radius-md)',
    background: isActive ? 'var(--blue-600)' : 'transparent',
    color: isActive
      ? 'var(--neutral-100)'
      : isDisabled
        ? 'var(--neutral-400)'
        : 'var(--neutral-700)',
    border: '1px solid',
    borderColor: isActive ? 'var(--blue-600)' : 'var(--neutral-300)',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    transition: 'all 150ms ease',
  });

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: 16,
        flexWrap: 'wrap',
        gap: 12,
      }}
    >
      <p style={{ fontSize: 13, color: 'var(--neutral-500)' }}>
        Showing page {page} of {totalPages} ({total.toLocaleString()} total)
      </p>
      <div style={{ display: 'flex', gap: 4 }}>
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
          style={buttonStyle(false, page === 1)}
        >
          ←
        </button>
        {pages().map((p, i) =>
          typeof p === 'string' ? (
            <span
              key={i}
              style={{
                minWidth: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--neutral-400)',
                fontSize: 13,
              }}
            >
              {p}
            </span>
          ) : (
            <button
              key={i}
              onClick={() => onPageChange(p)}
              style={buttonStyle(p === page)}
            >
              {p}
            </button>
          ),
        )}
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page === totalPages}
          style={buttonStyle(false, page === totalPages)}
        >
          →
        </button>
      </div>
    </div>
  );
};